import CreateCampaignPreview from "../CampaignPreview";
import BuildCampaignForm from "./BuildCampaignForm";
import {
  HStack,
  Spinner,
  Stack,
  useMediaQuery,
  VStack,
  Box,
  useToast,
} from "@chakra-ui/react";
import { EventType, FormProvider, useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import actions from "src/actions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { KYDScope } from "@common/interfaces/ICognito";
import { VersionedTemplates } from "common/email-templates";
import {
  KYDAdminFeed,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import { algoliasearch, SearchClient } from "algoliasearch";
import {
  KYDBlockData,
  KYDRenderedCampaignResponse,
} from "@common/interfaces/KYDCampaign";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { InstantSearch } from "react-instantsearch";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import { nanoid } from "nanoid";
import { S3Response } from "react-s3-uploader";
import { useFormWatcher } from "src/Manager/hooks/form";

export interface FormValues {
  name: string;
  additional_audiences?: string[];
  subject: string;
  subject_preview: string;
  preheader?: string;
  blocks: KYDBlockData[];
  send_at?: string;
  segments: {
    name: string;
    id: string;
    size: number;
    type?: "static" | "dynamic" | "system";
  }[];
  recalculate_size?: boolean;
  template_version: number;
  updated_at: string;
}

const CreateCampaign = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [emailHtmlCounter, setEmailHtmlCounter] = useState(0);
  const [user, setUser] = useState<KYDOrganizationUser | null>(null);
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const isMobile = useMediaQuery("(max-width: 768px)")[0];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const template = searchParams.get("template");
  const isNewTemplateCampaign = !id && !!template;
  const shouldRedirectToTemplates = !id && !template;
  const [organization, setOrganization] = useState<KYDOrganizationFull | null>(
    null
  );
  const toast = useToast();

  const [segmentsAlgoliaConfig, setSegmentsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  }>(null);

  const [eventsAlgoliaConfig, setEventsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  }>(null);

  const [externalEvents, setExternalEvents] = useState<
    KYDAdminFeed["external_events"]
  >([]);

  const [loading, setLoading] = useState({
    organization: true,
    user: true,
    externalEvents: true,
    savingCampaign: false,
    renderingEmail: true,
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTemplateReady, setIsTemplateReady] = useState(
    !isNewTemplateCampaign
  );
  const pendingSaveRef = useRef(false);

  const form = useForm<FormValues>({
    defaultValues: {
      blocks: [],
      segments: [],
      send_at: new Date().toISOString(),
      subject: "",
      preheader: "",
      name: "New Campaign",
      template_version: undefined,
      updated_at: undefined,
    },
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
  });

  const fetchSegmentsAlgoliaConfig = useCallback(async () => {
    const fetchConfig = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.SEGMENTS,
    });
    if (fetchConfig.events_config) {
      const search = algoliasearch(
        fetchConfig.events_config.app_id,
        fetchConfig.events_config.api_key
      );
      search.clearCache();
      setSegmentsAlgoliaConfig({
        indexName: fetchConfig.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const fetchExternalEvents = useCallback(async () => {
    setLoading((prev) => ({ ...prev, externalEvents: true }));
    try {
      setLoading((prev) => ({ ...prev, externalEvents: true }));
      const bio = await actions.ADMIN.fetchBio();
      setExternalEvents(bio.external_events);
    } catch (e) {
      console.error(e);
    }
    setLoading((prev) => ({ ...prev, externalEvents: false }));
  }, []);

  const fetchMe = useCallback(async () => {
    setLoading((prev) => ({ ...prev, user: true }));
    try {
      const me = await actions.ADMIN.fetchMe();
      setUser(me);
    } catch (err) {
      console.error(err);
    }
    setLoading((prev) => ({ ...prev, user: false }));
  }, []);

  const fetchOrganization = useCallback(async () => {
    setLoading((prev) => ({ ...prev, organization: true }));
    try {
      const organization = await actions.ADMIN.fetchOrganization();
      setOrganization(organization);
    } catch (err) {
      console.error(err);
    }
    setLoading((prev) => ({ ...prev, organization: false }));
  }, []);

  const fetchEventsAlgoliaConfig = useCallback(async () => {
    try {
      const response = await actions.ADMIN.fetchAlgoliaConfig();
      if (response.events_config) {
        const search = algoliasearch(
          response.events_config.app_id,
          response.events_config.api_key
        );
        search.clearCache();
        setEventsAlgoliaConfig({
          indexName: response.events_config.index_name,
          searchClient: search,
        });
      }
    } catch (err: any) {
      console.error(err);
    }
  }, []);

  const fetchEmailTemplate = useCallback(
    async (version: number) => {
      if (!template) {
        return;
      }

      setIsTemplateReady(false);

      try {
        const response = await actions.ADMIN.fetchEmailTemplate(
          template.toLowerCase(),
          version
        );

        if (version !== response.version) {
          form.setValue("template_version", response.version);
        }

        form.setValue(
          "blocks",
          response.blocks.map((b) => ({
            ...b,
            id: b.id || nanoid(),
          }))
        );
        setIsTemplateReady(true);
      } catch (err) {
        console.error(err);
        navigate("/campaigns/templates", { replace: true });
      }
    },
    [form, navigate, template]
  );

  const createCampaign = useCallback(
    async (values: FormValues, silent?: boolean) => {
      if (!values.template_version) {
        return;
      }

      try {
        if (!silent) {
          setLoading((prev) => ({ ...prev, savingCampaign: true }));
        }
        const response = await actions.ADMIN.createMarketingCampaign({
          name: values.name,
          subject: values.subject,
          preheader: values.preheader,
          blocks: values.blocks,
          send_at: values.send_at,
          template_version: values.template_version,
        });
        if (response.id) {
          navigate(`/campaigns/create?id=${response.id}`, {
            state: {
              blocks: values.blocks,
            },
            replace: true,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((prev) => ({ ...prev, savingCampaign: false }));
      }
    },
    []
  );

  const onFinishRendering = useCallback(() => {
    setLoading((prev) => ({ ...prev, renderingEmail: false }));
  }, [setLoading]);

  const updateCampaign = useCallback(
    async (id: string, values: FormValues, silent?: boolean) => {
      try {
        if (!silent) {
          setLoading((prev) => ({ ...prev, savingCampaign: true }));
        }

        const { campaign, html_string }: KYDRenderedCampaignResponse =
          await actions.ADMIN.updateMarketingCampaign({
            id,
            data: values,
          });

        //form.reset(campaign);
        setEmailHtml(html_string);

        if (emailHtml === html_string) {
          onFinishRendering();
        }

        form.setValue("updated_at", new Date().toISOString());
      } catch (err) {
        toast({
          title: "Error saving campaign",
          description: "Failed to save campaign",
          status: "error",
        });
      } finally {
        setLoading((prev) => ({ ...prev, savingCampaign: false }));
      }
    },
    [id, form, emailHtml, onFinishRendering]
  );

  const debouncedSave = useMemo(
    () =>
      debounce((values: FormValues) => {
        pendingSaveRef.current = true;
        if (id) {
          updateCampaign(id, values, true).finally(() => {
            pendingSaveRef.current = false;
            setHasUnsavedChanges(false);
          });
        } else {
          createCampaign(values, true).finally(() => {
            pendingSaveRef.current = false;
            setHasUnsavedChanges(false);
          });
        }
      }, 1000),
    [id, updateCampaign, createCampaign]
  );

  const fetchCampaign = useCallback(async () => {
    try {
      const { campaign, html_string }: KYDRenderedCampaignResponse =
        await actions.ADMIN.fetchMarketingCampaign(id);

      form.reset(campaign);
      setEmailHtml(html_string);
    } catch (err) {
      console.error(err);
    }
  }, [id, form]);

  const createPublicUploadUrl = useCallback(
    async ({
      file,
      callback,
    }: {
      file: File;
      callback: (params: S3Response) => void | Promise<void | string>;
    }) => {
      const response = await actions.ADMIN.createPublicUploadUrl(
        file,
        callback
      );
      return response as unknown as S3Response;
    },
    []
  );

  const handleNext = useCallback(async () => {
    const values = form.getValues();
    await updateCampaign(id, { ...values, recalculate_size: true });
    navigate(`/campaigns/create/review?id=${id}`);
  }, [id, navigate, form]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges || pendingSaveRef.current) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave? Changes are still being saved.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (shouldRedirectToTemplates) {
      navigate("/campaigns/templates", { replace: true });
    }
  }, [navigate, shouldRedirectToTemplates]);

  useEffect(() => {
    setIsTemplateReady(!isNewTemplateCampaign);
  }, [isNewTemplateCampaign]);

  const watchTemplateVersion = form.watch("template_version");

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
  }, [id, fetchCampaign]);

  useEffect(() => {
    fetchEmailTemplate(watchTemplateVersion);
  }, [watchTemplateVersion, fetchEmailTemplate]);

  // 1. Extract watcher callback to a named function
  const onFormChange = useCallback(
    (values: FormValues, changed: { name: string; type: EventType }) => {
      if (changed?.name === "updated_at") return;
      if (isNewTemplateCampaign && !isTemplateReady) return;
      if (!id && !values.template_version) return;

      if (values.name && changed?.name) {
        setHasUnsavedChanges(true);
        debouncedSave(values as FormValues);
        setLoading((prev) => ({ ...prev, renderingEmail: true }));
      }
    },
    [debouncedSave, id, isNewTemplateCampaign, isTemplateReady]
  );

  useFormWatcher(form, onFormChange);

  useEffect(() => {
    fetchMe();
    fetchOrganization();
    fetchExternalEvents();
    fetchEventsAlgoliaConfig();
    fetchSegmentsAlgoliaConfig();
  }, []);

  if (shouldRedirectToTemplates) {
    return null;
  }

  if (
    loading.organization ||
    loading.user ||
    loading.externalEvents ||
    (isNewTemplateCampaign && !isTemplateReady) ||
    !segmentsAlgoliaConfig?.searchClient ||
    !eventsAlgoliaConfig?.searchClient
  ) {
    return (
      <VStack
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        py={4}
      >
        <Spinner />
      </VStack>
    );
  }

  return (
    <VStack h="100%" w="100%">
      <FormProvider {...form}>
        <Stack
          direction={{ base: "column", md: "row" }}
          w="100%"
          h="100%"
          gap={0}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <InstantSearch
            searchClient={segmentsAlgoliaConfig?.searchClient}
            indexName={segmentsAlgoliaConfig?.indexName}
          >
            <BuildCampaignForm
              permissions={permissions}
              isMobile={isMobile}
              createUploadUrl={createPublicUploadUrl}
              loading={loading}
              handleNext={handleNext}
              organization={organization}
              externalEvents={externalEvents}
              searchClient={eventsAlgoliaConfig?.searchClient}
              indexName={eventsAlgoliaConfig?.indexName}
            />
          </InstantSearch>
          <CreateCampaignPreview
            showPreview={false}
            emailHtml={emailHtml || ""}
            emailHtmlCounter={emailHtmlCounter}
            onFinishRendering={onFinishRendering}
            view={view}
            loading={loading.renderingEmail}
            setView={setView}
            isMobile={false}
          />
        </Stack>
      </FormProvider>
    </VStack>
  );
};

export default CreateCampaign;
